import { BUILT_IN_TEMPLATES } from "@/lib/templates/built-ins";
import type { InvoiceTemplate } from "@/lib/templates/types";
import { useLiveQuery } from "dexie-react-hooks";
import { usePaidFeatureAccess } from "../pricing/entitlement-context";
import { db } from "./schema";
import type {
  CatalogItem,
  Customer,
  Invoice,
  RecurringInvoiceProfile,
  SellerProfile,
} from "./types";

/**
 * Live-binding React hooks for IndexedDB.
 * `useSellerProfile()` returns the active/default Brand Kit when set, then falls back
 * to the most recently updated profile for existing local data.
 */

export const ACTIVE_SELLER_PROFILE_META_KEY = "activeSellerProfileId";

export function useSellerProfile(): SellerProfile | undefined | null {
  // undefined = still loading; null = none exists; obj = found
  return useLiveQuery(async () => {
    const active = await db.meta.get(ACTIVE_SELLER_PROFILE_META_KEY);
    if (typeof active?.value === "string") {
      const activeProfile = await db.sellerProfiles.get(active.value);
      if (activeProfile) return activeProfile;
    }
    const list = await db.sellerProfiles.orderBy("updatedAt").reverse().limit(1).toArray();
    return list[0] ?? null;
  }, []);
}

export function useSellerProfiles(): SellerProfile[] | undefined {
  return useLiveQuery(() => db.sellerProfiles.orderBy("updatedAt").reverse().toArray(), []);
}

export async function setActiveSellerProfile(id: string): Promise<void> {
  await db.meta.put({ key: ACTIVE_SELLER_PROFILE_META_KEY, value: id });
}

export function useCustomers(): Customer[] | undefined {
  return useLiveQuery(() => db.customers.orderBy("updatedAt").reverse().toArray(), []);
}

export function useCatalogItems(includeArchived = false): CatalogItem[] | undefined {
  return useLiveQuery(async () => {
    const all = await db.catalogItems.orderBy("useCount").reverse().toArray();
    return includeArchived ? all : all.filter((i) => !i.archived);
  }, [includeArchived]);
}

export function useInvoices(sellerProfileId?: string): Invoice[] | undefined {
  return useLiveQuery(async () => {
    if (!sellerProfileId) {
      const invoices = await db.invoices.toArray();
      return invoices.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    }
    return db.invoices
      .where("sellerProfileId")
      .equals(sellerProfileId)
      .reverse()
      .sortBy("issueDate");
  }, [sellerProfileId]);
}

export function useStorageEstimate() {
  return useLiveQuery(async () => {
    if (!("storage" in navigator) || !("estimate" in navigator.storage)) {
      return { usage: 0, quota: 0 };
    }
    const e = await navigator.storage.estimate();
    return { usage: e.usage ?? 0, quota: e.quota ?? 0 };
  }, []);
}

export function useTemplates(): InvoiceTemplate[] | undefined {
  const premiumDesignsUnlocked = usePaidFeatureAccess("premiumDesigns");
  return useLiveQuery(async () => {
    const localTemplates = await db.templates.orderBy("updatedAt").reverse().toArray();
    return buildAvailableTemplates(localTemplates, premiumDesignsUnlocked);
  }, [premiumDesignsUnlocked]);
}

export function useLocalTemplates(): InvoiceTemplate[] | undefined {
  return useLiveQuery(() => db.templates.orderBy("updatedAt").reverse().toArray(), []);
}

export function useRecurringProfiles(): RecurringInvoiceProfile[] | undefined {
  return useLiveQuery(() => db.recurringProfiles.orderBy("updatedAt").reverse().toArray(), []);
}

export function buildAvailableTemplates(
  localTemplates: InvoiceTemplate[],
  premiumDesignsUnlocked: boolean,
): InvoiceTemplate[] {
  const builtIns = premiumDesignsUnlocked
    ? BUILT_IN_TEMPLATES
    : BUILT_IN_TEMPLATES.filter((template) => template.access !== "premium");
  const outputTemplates = premiumDesignsUnlocked ? localTemplates : [];

  return [...builtIns, ...outputTemplates];
}
